import Button from 'tio-reimagined/components/button';
import { eq, or } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { HStack, Section, VStack } from 'tio-reimagined/components/layout';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import downloadFile from 'tio-common/utils/download-file';
import { settleTasPaymentRequest, cancelTasPaymentRequest } from 'tio-common/requests/tas-payment';
import pageTitle from 'ember-page-title/helpers/page-title';
import Paginator from 'tio-reimagined/components/paginator';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import humanize from 'tio-common/helpers/humanize';
import Table from 'tio-reimagined/components/table';
import TioPageBreadcrumbs from 'tio-common/components/tio/page/breadcrumbs';
import type CompanyModel from 'tio-data/models/company';
import type PaymentModel from 'tio-data/models/tas-payment';
import type { IntlService } from 'ember-intl';
import type RouterService from '@ember/routing/router-service';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import { PhArrowFatLineDown } from 'ember-phosphor-icons';
import Select from 'tio-reimagined/components/select';

const PAYMENT_STATES = [
  'PROCESSING',
  'CREATED',
  'ABANDONED',
  'CANCELED',
  'PAID',
  'EMPTY',
  'ERRORED',
];

interface AdminTuitionAssistancePaymentsSignature {
  Args: {
    model: {
      tasPayments: PaymentModel[];
      total: number;
    };
    controller: {
      state: string;
      page: number;
    };
  };
}

export default class AdminTuitionAssistancePayments extends Component<AdminTuitionAssistancePaymentsSignature> {
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare intl: IntlService;

  @tracked selectedPayment?: PaymentModel;
  @tracked selectedCompany?: CompanyModel;

  @tracked createModalOpen: boolean = false;
  @tracked settleModalOpen: boolean = false;
  @tracked cancelModalOpen: boolean = false;
  @tracked isSettlingPayment: boolean = false;
  @tracked isCancelingPayment: boolean = false;

  get settleablePayments() {
    const { tasPayments } = this.args.model;
    return tasPayments.filter((payment) => payment.state === 'CREATED');
  }

  get paymentOptions() {
    return this.settleablePayments.map((payment) => {
      return {
        key: payment.id!,
        label: `${payment.id} - ${this.intl.formatDate(payment.paymentDate)}`,
        payment,
      };
    });
  }

  setStateFilter = (value: string | null) => {
    this.args.controller.state = value ?? '';
  };

  setSelectedPayment = (key: string | null) => {
    this.selectedPayment = this.settleablePayments.find((p) => p.id === key);
  };

  toggleSettleModal = () => {
    this.settleModalOpen = !this.settleModalOpen;
  };

  closeSettleModal = () => {
    delete this.selectedPayment;
    this.toggleSettleModal();
  };

  settlePayment = async () => {
    this.isSettlingPayment = true;
    const paymentId = this.selectedPayment?.id;
    if (!paymentId) return;
    await this.store.request(settleTasPaymentRequest(paymentId));
    this.isSettlingPayment = false;
    this.closeSettleModal();
    this.selectedPayment = undefined;
    this.router.refresh(this.router.currentRoute?.name);
  };

  toggleCancelModal = () => {
    this.cancelModalOpen = !this.cancelModalOpen;
  };

  closeCancelModal = () => {
    delete this.selectedPayment;
    this.toggleCancelModal();
  };

  cancelPayment = async () => {
    const paymentId = this.selectedPayment?.id;
    if (!paymentId) return;
    this.isCancelingPayment = true;
    await this.store.request(cancelTasPaymentRequest(paymentId));
    this.isCancelingPayment = false;
    this.closeCancelModal();
    this.selectedPayment = undefined;
    this.router.refresh(this.router.currentRoute?.name);
  };

  downloadPaymentFiles = async (paymentId: string) => {
    if (!paymentId) return;
    const adapter = this.store.adapterFor('tas-payment');
    // @ts-expect-error - Ember Data adapter property not typed on MinimumAdapterInterface
    const headers = adapter.headers;
    // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
    const url = [adapter.buildURL('tas-payments'), paymentId, 'download_files'].join('/');

    const response = await fetch(url, { headers });
    if (response.ok) {
      downloadFile(await response.blob(), 'tas_payment_files.zip', 'application/x-zip');
    }
  };

  <template>
    {{pageTitle (t "common.tuition_assistance.payments.default")}}
    <VStack>
      <TioPageBreadcrumbs as |b|>
        <b.crumb
          @route="authenticated.admin.tuition-assistance"
          @label={{t "tuition_assistance.default"}}
        />
        <b.crumb
          @route="authenticated.admin.tuition-assistance.payments"
          @label={{t "tuition_assistance.payments"}}
        />
      </TioPageBreadcrumbs>
      <Section>
        <:body>
          <VStack>
            <Select
              @label="Filter by status"
              @items={{PAYMENT_STATES}}
              @selected={{@controller.state}}
              @onChange={{this.setStateFilter}}
            />
            <HStack>
              <Button @intent="primary" {{on "click" this.toggleSettleModal}}>
                {{t "tuition_assistance.admin.payments.settle"}}
              </Button>
              <Button @intent="danger" @appearance="outlined" {{on "click" this.toggleCancelModal}}>
                {{t "tuition_assistance.admin.payments.cancel"}}
              </Button>
            </HStack>
          </VStack>
        </:body>
      </Section>
      <Section>
        <:body>
          <Table @isLoading={{false}}>
            <:thead as |options|>
              <options.tr>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.id"
                  }}</options.th>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.payment_date"
                  }}</options.th>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.status"
                  }}</options.th>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.type"
                  }}</options.th>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.created_by"
                  }}</options.th>
                <options.th>{{t
                    "common.tuition_assistance.payments.attribute_label.download"
                  }}</options.th>
              </options.tr>
            </:thead>
            <:tbody as |options|>
              {{#each @model.tasPayments as |payment|}}
                <options.tr>
                  <options.td>{{payment.id}}</options.td>
                  <options.td>
                    {{#if payment.paymentDate}}
                      {{safeFormatDate
                        payment.paymentDate
                        month="long"
                        day="2-digit"
                        year="numeric"
                      }}
                    {{/if}}
                  </options.td>
                  <options.td>{{humanize payment.state}}</options.td>
                  <options.td>{{payment.paymentType}}</options.td>
                  <options.td>{{payment.createdBy}}</options.td>
                  <options.td>
                    {{! @glint-expect-error}}
                    <button type="button" {{on "click" (fn this.downloadPaymentFiles payment.id)}}>
                      <PhArrowFatLineDown
                        @size="24"
                        @weight="fill"
                        class="flex flex-col justify-center ml-4 text-ocean-600"
                      />
                    </button>
                  </options.td>
                </options.tr>
              {{/each}}
              {{#if (eq @model.tasPayments.length 0)}}
                <options.tr>
                  <options.td colspan="6">
                    {{t "common.no_data"}}
                  </options.td>
                </options.tr>
              {{/if}}
            </:tbody>
            <:tfoot as |options|>
              <options.tr>
                <td colspan="6">
                  <Paginator @totalItems={{@model.tasPayments.length}} @summary={{true}} />
                </td>
              </options.tr>
            </:tfoot>
          </Table>
        </:body>
      </Section>
    </VStack>
    <Modal @isOpen={{this.settleModalOpen}} @onClose={{this.closeSettleModal}} class="min-h-64">
      <:header>{{t "tuition_assistance.admin.payments.settle"}}</:header>
      <:body>
        <Select
          @label="Payment Runs"
          @items={{this.paymentOptions}}
          @selected={{or this.selectedPayment.id ""}}
          @onChange={{this.setSelectedPayment}}
          @searchable={{true}}
        />
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.isSettlingPayment}}
          @onCancel={{this.closeSettleModal}}
          @onSubmit={{this.settlePayment}}
          @submitText="Settle Payment"
        />
      </:footer>
    </Modal>
    <Modal @isOpen={{this.cancelModalOpen}} @onClose={{this.closeCancelModal}} class="min-h-64">
      <:header>{{t "tuition_assistance.admin.payments.cancel"}}</:header>
      <:body>
        <Select
          @label="Payment Runs"
          @items={{this.paymentOptions}}
          @selected={{or this.selectedPayment.id ""}}
          @onChange={{this.setSelectedPayment}}
          @searchable={{true}}
        />
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.isCancelingPayment}}
          @onCancel={{this.closeCancelModal}}
          @onSubmit={{this.cancelPayment}}
          @submitText="Cancel Payment"
        />
      </:footer>
    </Modal>
  </template>
}
